// -------------------------------
// TRAINER INTERFACES
// -------------------------------
export interface PendingTrainer {
    name?: string;
    trainerType?: "Employee" | "Third_party";
    instituteName?: string;
    phone?: string;
    email?: string;
    dob?: string;
    address?: string;
    description?: string;
    status?: string; // active | inactive
}

export interface PendingDeleteTrainer {
    awaitingName?: boolean;
}

export const startCreateTrainerFlow = (
    setPendingTrainer: (data: PendingTrainer | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        setPendingTrainer({ name: nameFromIntent });

        pushAIChat(`Is "${nameFromIntent}" an Employee trainer or a Third Party trainer?`);
        speak(`Is ${nameFromIntent} an employee trainer or a third party trainer?`);
        return;
    }

    setPendingTrainer({});
    pushAIChat("Sure! What is the trainer's name?");
    speak("What is the trainer's name?");
    window.dispatchEvent(new CustomEvent("ai-show-suggestions", { detail: { type: "employee_trainer" } }));
};

export const processPendingTrainer = (
    text: string,
    pending: PendingTrainer,
    setPendingTrainer: (data: PendingTrainer | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — Name
    if (!pending.name) {
        const name = text.replace(/[^a-zA-Z ]/g, "").trim();

        if (!name) {
            pushAIChat("Please provide a valid trainer name.");
            speak("Please provide a valid trainer name.");
            return;
        }

        setPendingTrainer({ ...pending, name });

        pushAIChat(`Is "${name}" an Employee trainer or a Third Party trainer?`);
        speak(`Is ${name} an employee trainer or a third party trainer?`);
        return;
    }

    // STEP 2 — Trainer Type
    if (!pending.trainerType) {
        const trainerType =
            text.toLowerCase().includes("third")
                ? "Third_party"
                : "Employee";

        setPendingTrainer({ ...pending, trainerType });

        if (trainerType === "Third_party") {
            pushAIChat("What is the institute name?");
            speak("What is the institute name?");
            return;
        }

        pushAIChat("Please provide the phone number.");
        speak("Please provide the phone number.");
        return;
    }

    // STEP 3 — Institute Name (only for third party)
    if (pending.trainerType === "Third_party" && !pending.instituteName) {
        setPendingTrainer({ ...pending, instituteName: text });

        pushAIChat("Please provide the phone number.");
        speak("Please provide the phone number.");
        return;
    }

    // STEP 4 — Phone
    if (!pending.phone) {
        const phone = text.replace(/\D/g, "");

        if (!/^\d{10}$/.test(phone)) {
            pushAIChat("Please provide a valid 10-digit phone number.");
            speak("Please provide a valid phone number.");
            return;
        }

        setPendingTrainer({ ...pending, phone });

        pushAIChat("Please provide the email address.");
        speak("Please provide the email address.");
        return;
    }

    // STEP 5 — Email
    if (!pending.email) {
        const email = text.trim();

        if (!/^\S+@\S+\.\S+$/.test(email)) {
            pushAIChat("Please provide a valid email address.");
            speak("Please provide a valid email address.");
            return;
        }

        setPendingTrainer({ ...pending, email });

        pushAIChat("Please provide date of birth (YYYY-MM-DD).");
        speak("Please provide date of birth.");
        return;
    }

    // STEP 6 — DOB
    if (!pending.dob) {
        setPendingTrainer({ ...pending, dob: text });

        pushAIChat("Please provide the address.");
        speak("Please provide the address.");
        return;
    }

    // STEP 7 — Address
    if (!pending.address) {
        setPendingTrainer({ ...pending, address: text });

        pushAIChat("Any description for this trainer?");
        speak("Any description?");
        return;
    }

    // STEP 8 — Description
    if (!pending.description) {
        setPendingTrainer({ ...pending, description: text });

        pushAIChat("Should the trainer be active or inactive?");
        speak("Should the trainer be active or inactive?");
        return;
    }

    // STEP 9 — Status (FINAL)
    if (!pending.status) {
        const status = text.toLowerCase().includes("inactive")
            ? "inactive"
            : "active";

        pushAIChat(`Perfect! Creating trainer "${pending.name}"...`);
        speak(`Creating trainer ${pending.name}.`);

        window.dispatchEvent(
            new CustomEvent("ai-create-trainer", {
                detail: {
                    ...pending,
                    status
                }
            })
        );

        setPendingTrainer(null);
    }
};

export const startDeleteTrainerFlow = (
    setPendingDelete: (data: PendingDeleteTrainer | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        pushAIChat(`Deleting trainer "${nameFromIntent}"...`);
        speak(`Deleting trainer ${nameFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-trainer", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingName: true });
    pushAIChat("Which trainer would you like to delete?");
    speak("Which trainer would you like to delete?");
};

export const processPendingDeleteTrainer = (
    text: string,
    pending: PendingDeleteTrainer,
    setPendingDelete: (data: PendingDeleteTrainer | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (pending.awaitingName) {
        const name = text.replace(/[^a-zA-Z ]/g, "").trim();

        if (!name) {
            pushAIChat("Please provide a valid trainer name.");
            speak("Please provide a valid trainer name.");
            return;
        }

        pushAIChat(`Deleting trainer "${name}"...`);
        speak(`Deleting trainer ${name}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-trainer", {
                detail: { name }
            })
        );

        setPendingDelete(null);
    }
};

